import React, { FC } from 'react';

import { Box } from '@mui/system';
import { Typography } from '@mui/material';

import { ProfessionalReplacementTypes } from '@pharmaplan/common';
import {
  ChipType,
  ReplacementLabels,
  SkeletonVariants,
} from '../../../helpers/Constants';
import useSelfService from '../../../hooks/useSelfService';
import ReplacementTag from '../ReplacementTag';
import strings from '../../../localization';
import WithSkeleton from '../WithSkeleton';

import HourlyRateChipLabel from './HourlyRateChipLabel';
import styles from './styles';

interface IAdminPharmacyFooter {
  software?: string;
  successAction: string;
  pharmacyHourlyRate?: number;
  pharmacistHourlyRate?: number;
  replacementType?: ProfessionalReplacementTypes;
  customHourlyLabel?: string;
  showRates?: boolean;
  showPharmacyHourlyRate?: boolean;
  customPharmacistChip?: React.ReactNode | null;
  customPharmacyChip?: React.ReactNode | null;
  isPromoBooking?: boolean;
  pharmacyInfoText?: string;
}

const {
  pharmacyHourlyRate: pharmacyHourlyChip,
  hourlyRate,
  promoBooking,
} = ChipType;

const AdminPharmacyFooter: FC<IAdminPharmacyFooter> = ({
  software,
  pharmacyHourlyRate,
  isPromoBooking,
  pharmacistHourlyRate,
  successAction,
  replacementType,
  customHourlyLabel,
  showPharmacyHourlyRate = false,
  customPharmacistChip,
  pharmacyInfoText,
  showRates = true,
  customPharmacyChip,
}) => {
  const SkeletonTypography = WithSkeleton({
    successAction,
    variant: SkeletonVariants.text,
  })(Typography);

  const { isSelfService } = useSelfService();
  const label = strings.formatString(
    strings.hourlyRateFormat,
    ReplacementLabels()[
      replacementType ?? ProfessionalReplacementTypes.pharmacist
    ],
  ) as string;
  const isReplacementDefined = replacementType !== undefined;

  const showPharmacyRate = !isSelfService || showPharmacyHourlyRate;
  const showPharmacistRate = !!pharmacistHourlyRate || !software;

  return (
    <Box sx={styles.mainContainer}>
      {showRates && (
        <>

          {customPharmacistChip
            ?? (showPharmacistRate ? (
              <HourlyRateChipLabel
                type={hourlyRate}
                label={customHourlyLabel || label}
                rate={pharmacistHourlyRate}
                successAction={successAction}
              />
            ) : (
              <Box sx={styles.itemContainer}>
                <SkeletonTypography sx={styles.title}>
                  {strings.software}
                </SkeletonTypography>
                <SkeletonTypography sx={styles.value}>
                  {software}
                </SkeletonTypography>
              </Box>
            ))}
          {customPharmacyChip
            ?? (showPharmacyRate && (
              <HourlyRateChipLabel
                infoText={pharmacyInfoText}
                type={isPromoBooking ? promoBooking : pharmacyHourlyChip}
                label={strings.pharmacyHourlyRate}
                rate={pharmacyHourlyRate}
                successAction={successAction}
              />
            ))}
        </>
      )}
      {!!isReplacementDefined && (
        <Box sx={[styles.itemContainer]}>
          <ReplacementTag
            replacementType={replacementType}
            successAction={successAction}
          />
        </Box>
      )}
    </Box>
  );
};

export default AdminPharmacyFooter;
